/* Purchasing lists contain public product references only. Prices and stock are resolved at review. */
(function (root, factory) {
  const core = factory();
  if (typeof module === "object" && module.exports) module.exports = core;
  if (!root?.document || root.SaberSavedLists) return;
  const document = root.document;
  const ui = value => root.SaberTranslateUi ? root.SaberTranslateUi(value) : value;
  const configElement = document.getElementById("SaberShoppingToolsContext");
  let context;
  try {
    context = JSON.parse(
      configElement?.dataset.context || configElement?.textContent || "{}",
    );
  } catch {
    return;
  }
  if (!context.shop) return;
  let local;
  let session;
  try {
    local = root.localStorage;
  } catch {
    /* Privacy settings may block storage access. */
  }
  try {
    session = root.sessionStorage;
  } catch {
    /* Review handoff will report the failure. */
  }
  const store = core.createStore(local, context);
  const emit = () =>
    document.dispatchEvent(new CustomEvent("saber:lists-changed"));
  const node = (tag, text, attributes = {}, raw = false) => {
    const element = document.createElement(tag);
    if (text !== undefined) element.textContent = raw ? text : ui(text);
    Object.entries(attributes).forEach(([key, value]) =>
      element.setAttribute(key, String(["placeholder", "aria-label", "title"].includes(key) ? ui(value) : value)),
    );
    return element;
  };
  const button = (label, action, secondary = false) => {
    const element = node("button", label, {
      type: "button",
      class: "ss-list-button" + (secondary ? " ss-list-button--secondary" : ""),
    });
    element.addEventListener("click", action);
    return element;
  };
  const types = ["Job", "Property", "Unit", "Facility", "Personal"];
  const field = (label, input) => {
    const wrapper = node("label", undefined, { class: "ss-list-field" });
    wrapper.append(node("span", label), input);
    return wrapper;
  };
  const typeSelect = (value = "Job") => {
    const select = node("select");
    types.forEach((type) =>
      select.append(node("option", type, { value: type })),
    );
    select.value = value;
    return select;
  };
  const report = (element, error, role = "alert") => {
    element.textContent = ui(
      error instanceof Error ? error.message : String(error));
    element.setAttribute("role", role);
    element.hidden = false;
  };
  const download = (filename, value) => {
    const url = URL.createObjectURL(
      new Blob([value], { type: "application/json" }),
    );
    const anchor = node("a", undefined, { href: url, download: filename });
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const stageForQuickOrder = (items) => {
    core.stageItems(session, context, items);
    return true;
  };
  const closeDialog = (dialog, opener) => {
    dialog.close();
    dialog.remove();
    if (opener?.isConnected) opener.focus();
  };
  function openSaveDialog(inputItems) {
    const items = core.normalizeItems(inputItems);
    if (!items.length) throw new Error("Choose at least one product to save.");
    document.querySelector(".ss-list-dialog")?.remove();
    const opener = document.activeElement;
    const dialog = node("dialog", undefined, {
      class: "ss-list-dialog",
      "aria-labelledby": "SaberSaveListTitle",
    });
    const form = node("form");
    const heading = node("div", undefined, { class: "ss-list-dialog-heading" });
    heading.append(
      node("h2", "Save to a purchasing list", { id: "SaberSaveListTitle" }),
      button("Close", () => closeDialog(dialog, opener), true),
    );
    const info = node(
      "p",
      `${items.length} product${items.length === 1 ? "" : "s"}. Saved on this device for this account and company location.`,
    );
    const select = node("select", undefined, { name: "list" });
    select.append(node("option", "Create a new list", { value: "" }));
    store
      .getState()
      .lists.forEach((list) =>
        select.append(
          node("option", `${list.name} · ${ui(list.type)}`, { value: list.id }, true),
        ),
      );
    const name = node("input", undefined, {
      name: "name",
      maxlength: "80",
      placeholder: "e.g. Main Street renovation",
      required: "",
    });
    const type = typeSelect();
    const newFields = node("div", undefined, { class: "ss-list-new-fields" });
    newFields.append(field("List name", name), field("Organize by", type));
    select.addEventListener("change", () => {
      newFields.hidden = Boolean(select.value);
      name.required = !select.value;
    });
    const status = node("p", undefined, {
      role: "alert",
      class: "ss-list-error",
      hidden: "",
    });
    const actions = node("div", undefined, { class: "ss-list-actions" });
    const submit = node("button", "Save products", {
      type: "submit",
      class: "ss-list-button",
    });
    const view = node("a", "View purchasing lists", {
      href: context.savedListsUrl,
      class: "ss-list-button ss-list-button--secondary",
    });
    actions.append(submit, view);
    form.append(
      heading,
      info,
      field("Choose a list", select),
      newFields,
      status,
      actions,
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      try {
        let id = select.value;
        if (id) store.addItems(id, items);
        else id = store.create(name.value, type.value, items);
        emit();
        info.textContent = ui(`Products saved to “${store.getState().lists.find((list) => list.id === id).name}” on this device.`);
        select.closest("label").hidden = true;
        newFields.hidden = true;
        status.hidden = true;
        submit.hidden = true;
        view.focus();
      } catch (error) {
        report(status, error);
      }
    });
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeDialog(dialog, opener);
    });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) closeDialog(dialog, opener);
    });
    dialog.append(form);
    document.body.append(dialog);
    dialog.showModal();
    select.focus();
    return true;
  }
  class SavedLists extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.activeId = "";
      this.update = () => this.render();
      document.addEventListener("saber:lists-changed", this.update);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener("saber:lists-changed", this.update);
      this.initialized = false;
    }
    render() {
      this.replaceChildren();
      const lists = store.getState().lists;
      if (!lists.some((list) => list.id === this.activeId))
        this.activeId = lists[0]?.id || "";
      this.status = node("p", undefined, {
        role: "alert",
        class: "ss-list-error",
        hidden: "",
      });
      if (store.readError) report(this.status, store.readError);
      const toolbar = node("div", undefined, { class: "ss-lists-toolbar" });
      const backup = button(
        "Export all lists",
        () => download("saber-purchasing-lists.json", store.export()),
        true,
      );
      backup.disabled = !lists.length;
      const importFile = node("input", undefined, {
        type: "file",
        accept: ".json,application/json",
        class: "ss-list-file",
      });
      const importLabel = field("Import lists from a JSON backup", importFile);
      importFile.addEventListener("change", async () => {
        const file = importFile.files[0];
        if (!file) return;
        try {
          if (file.size > core.MAX_BYTES)
            throw new Error(
              "This backup is too large. Use a file smaller than 1 MB.",
            );
          const ids = store.import(await file.text());
          this.activeId = ids[0] || this.activeId;
          emit();
          report(
            this.status,
            `${ids.length} list${ids.length === 1 ? "" : "s"} imported. Existing lists were kept.`,
            "status",
          );
        } catch (error) {
          report(this.status, error);
        }
      });
      toolbar.append(backup, importLabel);
      if (store.readError?.code === "DAMAGED") {
        toolbar.append(
          button(
            "Reset damaged list storage",
            () => {
              if (
                !root.confirm(ui(
                  "The saved-list data in this browser cannot be read. Reset it and start again? Exported backups are not affected.",
                ))
              )
                return;
              try {
                store.reset();
                emit();
              } catch (error) {
                report(this.status, error);
              }
            },
            true,
          ),
        );
      }
      const layout = node("div", undefined, { class: "ss-lists-layout" });
      const sidebar = node("aside", undefined, {
        class: "ss-lists-sidebar",
        "aria-label": "Your purchasing lists",
      });
      sidebar.append(node("h2", `Your lists (${lists.length})`));
      const listNav = node("div", undefined, { class: "ss-lists-nav" });
      lists.forEach((list) => {
        const item = button(
          "",
          () => {
            this.activeId = list.id;
            this.render();
          },
          true,
        );
        item.setAttribute("aria-pressed", String(list.id === this.activeId));
        item.append(
          node("strong", list.name, {}, true),
          node("span", `${list.type} · ${list.items.length} products`),
        );
        listNav.append(item);
      });
      const create = node("form", undefined, { class: "ss-list-create" });
      const name = node("input", undefined, {
        name: "new-list-name",
        required: "",
        maxlength: "80",
        placeholder: "e.g. Building A, Unit 12",
      });
      const type = typeSelect();
      create.append(
        node("h3", "Create a list"),
        field("List name", name),
        field("Organize by", type),
        node("button", "Create list", {
          type: "submit",
          class: "ss-list-button",
        }),
      );
      create.addEventListener("submit", (event) => {
        event.preventDefault();
        try {
          this.activeId = store.create(name.value, type.value);
          emit();
        } catch (error) {
          report(this.status, error);
        }
      });
      sidebar.append(listNav, create);
      layout.append(
        sidebar,
        this.renderList(lists.find((list) => list.id === this.activeId)),
      );
      this.append(this.status, toolbar, layout);
    }
    renderList(list) {
      const panel = node("div", undefined, { class: "ss-list-panel" });
      if (!list) {
        panel.append(
          node("span", "Your next project starts here", {
            class: "ss-lists-eyebrow",
          }),
          node("h2", "A place for every supply list"),
          node(
            "p",
            "Create a list for a job, property, unit or facility. Save products from a product page or after reviewing SKUs in Quick order.",
          ),
          node("a", "Find products with Quick order", {
            href: context.quickOrderUrl,
            class: "ss-list-button",
          }),
        );
        return panel;
      }
      const title = node("div", undefined, { class: "ss-list-panel-heading" });
      const titleText = node("div");
      titleText.append(
        node("span", list.type, { class: "ss-lists-eyebrow" }),
        node("h2", list.name, {}, true),
      );
      title.append(
        titleText,
        button(
          "Export this list",
          () => download("saber-purchasing-list.json", store.export(list.id)),
          true,
        ),
      );
      panel.append(title);
      const rename = node("details", undefined, { class: "ss-list-details" });
      rename.append(node("summary", "Edit list name or type"));
      const edit = node("form", undefined, { class: "ss-list-edit" });
      const name = node("input", undefined, {
        required: "",
        maxlength: "80",
        value: list.name,
      });
      const type = typeSelect(list.type);
      edit.append(
        field("List name", name),
        field("Organize by", type),
        node("button", "Save changes", {
          type: "submit",
          class: "ss-list-button ss-list-button--secondary",
        }),
      );
      edit.addEventListener("submit", (event) => {
        event.preventDefault();
        try {
          store.rename(list.id, name.value, type.value);
          emit();
        } catch (error) {
          report(this.status, error);
        }
      });
      rename.append(edit);
      panel.append(rename);
      if (list.items.length) {
        const table = node("table", undefined, { class: "ss-list-table" });
        const head = node("thead");
        const headRow = node("tr");
        ["Product / SKU", "Quantity", "Remove"].forEach((text) =>
          headRow.append(node("th", text, { scope: "col" })),
        );
        head.append(headRow);
        const body = node("tbody");
        list.items.forEach((item) => {
          const row = node("tr");
          const product = node("td");
          const title = item.title || item.sku || ui("Saved product");
          product.append(
            item.url
              ? node("a", title, { href: item.url }, true)
              : node("strong", title, {}, true),
          );
          product.append(
            node(
              "span",
              item.sku ? `SKU: ${item.sku}` : `Variant: ${item.variantId}`,
              { class: "ss-list-sku" },
            ),
          );
          const quantityCell = node("td");
          const quantity = node("input", undefined, {
            type: "number",
            min: "1",
            max: "99999",
            step: "1",
            value: item.quantity,
            "aria-label": `Quantity for ${title}`,
            inputmode: "numeric",
          });
          quantity.addEventListener("change", () => {
            try {
              store.updateQuantity(list.id, item, quantity.value);
              emit();
            } catch (error) {
              quantity.value = item.quantity;
              report(this.status, error);
            }
          });
          quantityCell.append(quantity);
          const remove = node("td");
          const removeButton = button(
            "Remove",
            () => {
              try {
                store.removeItem(list.id, item);
                emit();
              } catch (error) {
                report(this.status, error);
              }
            },
            true,
          );
          removeButton.setAttribute("aria-label", ui(`Remove ${title}`));
          remove.append(removeButton);
          row.append(product, quantityCell, remove);
          body.append(row);
        });
        table.append(head, body);
        panel.append(table);
      } else
        panel.append(
          node(
            "p",
            "This list is ready for products. Use “Save to a purchasing list” on product pages or save your reviewed Quick order products.",
            { class: "ss-list-empty" },
          ),
        );
      panel.append(
        node(
          "p",
          "Current prices, availability and quantity requirements are checked in Quick order before you add anything to your cart.",
          { class: "ss-list-review-note" },
        ),
      );
      const actions = node("div", undefined, { class: "ss-list-actions" });
      const review = button("Review & order this list", () => {
        try {
          store.reload();
          if (store.readError) throw store.readError;
          const currentList = store
            .getState()
            .lists.find((saved) => saved.id === list.id);
          if (!currentList)
            throw new Error(
              "This list is no longer available. Refresh the page to see your latest lists.",
            );
          stageForQuickOrder(currentList.items);
          root.location.assign(context.quickOrderUrl + "#from-saved-list");
        } catch (error) {
          report(this.status, error);
        }
      });
      review.disabled = !list.items.length;
      const removeList = button(
        "Delete list",
        () => {
          if (!root.confirm(ui(`Delete “${list.name}” from this device?`))) return;
          try {
            store.delete(list.id);
            emit();
          } catch (error) {
            report(this.status, error);
          }
        },
        true,
      );
      actions.append(
        review,
        node("a", "Find more products", {
          href: context.quickOrderUrl,
          class: "ss-list-button ss-list-button--secondary",
        }),
        removeList,
      );
      panel.append(actions);
      return panel;
    }
  }
  class ListSave extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelector("button")?.addEventListener("click", () => {
        const status = this.querySelector(".ss-list-inline-status");
        try {
          const form = document.getElementById(this.dataset.formId);
          const variantId = form?.elements.namedItem("id")?.value;
          const variants = JSON.parse(
            this.querySelector("[data-list-variants]").dataset.listVariants,
          );
          // A newly selected high-variant product may not be in Liquid's first variant page.
          // Keep its native public form reference; Quick order verifies the exact product anew.
          const variant =
            variants.find((value) => String(value.id) === String(variantId)) ||
            (/^[1-9]\d{0,19}$/.test(String(variantId))
              ? { id: variantId, sku: "", title: "", minimum: 1 }
              : null);
          if (this.hidden || !variant || form.elements.namedItem("id").disabled)
            throw new Error("Select a product option before saving.");
          const quantityField = form.elements.namedItem("quantity");
          const quantity = quantityField
            ? quantityField.value
            : variant.minimum || 1;
          openSaveDialog([
            {
              sku: variant.sku,
              variantId: variant.id,
              quantity,
              title:
                this.dataset.productTitle +
                (variant.title && variant.title !== "Default Title"
                  ? ` — ${variant.title}`
                  : ""),
              url: this.dataset.productUrl,
            },
          ]);
          status.textContent = "";
        } catch (error) {
          report(status, error);
        }
      });
    }
  }
  root.SaberSavedLists = Object.freeze({
    readLists: () => {
      let current;
      try {
        const element = document.getElementById("SaberShoppingToolsContext");
        current = JSON.parse(element?.dataset.context || element?.textContent || "{}");
      } catch { throw new Error("Your shopping account changed. Refresh this page."); }
      if (["shop", "customer", "company", "location"].some(key => String(current[key] || "") !== String(context[key] || "")))
        throw new Error("Your account or company location changed. Refresh this page.");
      store.reload();
      if (store.readError) throw store.readError;
      return store.getState().lists;
    },
    openSaveDialog,
    stageForQuickOrder,
    consumeStagedItems: () => core.consumeItems(session, context),
  });
  document.addEventListener("saber:save-list", (event) => {
    try {
      openSaveDialog(event.detail?.items);
      event.preventDefault();
    } catch (error) {
      root.dispatchEvent(
        new CustomEvent("saber:lists-error", {
          detail: { message: error.message },
        }),
      );
    }
  });
  root.customElements.define("saber-saved-lists", SavedLists);
  root.customElements.define("saber-list-save", ListSave);
  root.addEventListener("storage", (event) => {
    if (event.key === store.key) {
      store.reload();
      emit();
    }
  });
  document.dispatchEvent(new CustomEvent("savedlists:ready"));
})(typeof window !== "undefined" ? window : null, function () {
  const VERSION = 1;
  const MAX_BYTES = 1024 * 1024;
  const MAX_LISTS = 50;
  const MAX_ITEMS = 100;
  const MAX_TOTAL_ITEMS = 1000;
  const TYPES = ["Job", "Property", "Unit", "Facility", "Personal"];
  const copy = (value) => JSON.parse(JSON.stringify(value));
  const fail = (message) => {
    throw new Error(message);
  };
  const text = (value, maximum) =>
    typeof value === "string"
      ? value
          .replace(/[\u0000-\u001f\u007f]/g, " ")
          .trim()
          .slice(0, maximum)
      : "";
  const identity = (value) =>
    typeof value === "number" && !Number.isSafeInteger(value)
      ? ""
      : /^[1-9]\d{0,19}$/.test(String(value))
        ? String(value)
        : "";
  const scopeKey = (context, type = "lists") => {
    if (!context?.shop)
      fail("The shopping tools are unavailable. Please refresh the page.");
    const buyer = identity(context.customer) || "guest";
    const company = buyer === "guest" ? "" : identity(context.company);
    const location = buyer === "guest" ? "" : identity(context.location);
    return [
      "saber",
      type,
      "v1",
      context.shop,
      buyer,
      company || "none",
      location || "none",
    ]
      .map((part) => encodeURIComponent(part))
      .join(":");
  };
  const quantity = (value) => {
    if (
      !/^\d+$/.test(String(value)) ||
      !Number.isSafeInteger(Number(value)) ||
      Number(value) < 1 ||
      Number(value) > 99999
    )
      fail("Use a whole-number quantity between 1 and 99,999.");
    return Number(value);
  };
  const productUrl = (value) => {
    const url = text(value, 600);
    return /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?products\/[^\s/?#\\]+(?:\?[^\s#\\]*)?$/i.test(
      url,
    )
      ? url
      : "";
  };
  const normalizeItems = (items) => {
    if (!Array.isArray(items) || items.length > MAX_ITEMS)
      fail(`Use no more than ${MAX_ITEMS} products in each list.`);
    const normalized = [];
    items.forEach((item) => {
      if (!item || typeof item !== "object")
        fail("A saved product could not be read.");
      const sku = text(item.sku, 255);
      const variantId = identity(item.variantId);
      const url = productUrl(item.url);
      if (!sku && !(variantId && url))
        fail(
          "Each product needs a SKU or a valid product and variant reference.",
        );
      const next = {
        sku,
        variantId,
        quantity: quantity(item.quantity),
        title: text(item.title, 240),
        url,
      };
      const existing = normalized.find((saved) =>
        variantId
          ? saved.variantId === variantId
          : !saved.variantId && saved.sku.toLowerCase() === sku.toLowerCase(),
      );
      if (existing)
        existing.quantity = quantity(existing.quantity + next.quantity);
      else normalized.push(next);
    });
    return normalized;
  };
  const listName = (value) =>
    text(value, 80) || fail("Enter a name for this purchasing list.");
  const listType = (value) =>
    TYPES.includes(value) ? value : fail("Choose a supported list type.");
  const validateState = (input) => {
    if (
      !input ||
      input.version !== VERSION ||
      !Array.isArray(input.lists) ||
      input.lists.length > MAX_LISTS
    )
      fail(
        "This is not a supported purchasing-list backup (version 1, up to 50 lists).",
      );
    let total = 0;
    const ids = new Set();
    const lists = input.lists.map((list) => {
      if (!list || typeof list !== "object")
        fail("A list in this backup could not be read.");
      const id = text(list.id, 100);
      if (!id || ids.has(id))
        fail("This backup contains invalid or duplicate list identifiers.");
      ids.add(id);
      const items = normalizeItems(list.items);
      total += items.length;
      if (total > MAX_TOTAL_ITEMS)
        fail(
          "This browser can hold up to 1,000 saved products. Export or remove older lists first.",
        );
      return {
        id,
        name: listName(list.name),
        type: listType(list.type),
        items,
      };
    });
    return { version: VERSION, lists };
  };
  const parseBackup = (raw) => {
    if (typeof raw !== "string" || raw.length > MAX_BYTES)
      fail("Use a purchasing-list JSON backup smaller than 1 MB.");
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      fail(
        "This file is not valid JSON. Choose an exported purchasing-list backup.",
      );
    }
    return validateState(parsed);
  };
  const storageError = () =>
    new Error(
      "Your browser could not save this change. Allow browser storage or free some space, then try again. Existing saved lists have been kept.",
    );
  function createStore(storage, context) {
    const key = scopeKey(context);
    let state = { version: VERSION, lists: [] };
    let readError = null;
    const reload = () => {
      let raw;
      try {
        if (!storage) throw storageError();
        raw = storage.getItem(key);
      } catch {
        readError = storageError();
        readError.code = "STORAGE";
        return;
      }
      try {
        const next = raw ? parseBackup(raw) : { version: VERSION, lists: [] };
        state = next;
        readError = null;
      } catch (error) {
        readError = error;
        readError.code = "DAMAGED";
        readError.message =
          "The saved-list data in this browser could not be read. It has not been overwritten. Restore a backup after resetting the damaged list storage.";
      }
    };
    reload();
    let counter = 0;
    const id = () =>
      `list-${Date.now().toString(36)}-${(++counter).toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const commit = (next, reset = false) => {
      if (!reset && readError) throw readError;
      const valid = validateState(next);
      const raw = JSON.stringify(valid);
      if (raw.length > MAX_BYTES)
        fail(
          "These lists exceed this browser’s 1 MB limit. Export and remove older lists first.",
        );
      try {
        if (!storage) throw storageError();
        storage.setItem(key, raw);
      } catch {
        throw storageError();
      }
      state = valid;
      readError = null;
    };
    const update = (listId, change) => {
      reload();
      const next = copy(state);
      const list = next.lists.find((item) => item.id === listId);
      if (!list)
        fail("This list is no longer available. Please select another list.");
      change(list);
      commit(next);
    };
    const matchingItem = (list, reference) => {
      const expected = JSON.stringify(normalizeItems([reference])[0]);
      const index = list.items.findIndex(
        (item) => JSON.stringify(item) === expected,
      );
      if (index < 0)
        fail(
          "This product changed in another tab. Refresh the page before editing it again.",
        );
      return index;
    };
    return {
      key,
      get readError() {
        return readError;
      },
      reload,
      getState: () => copy(state),
      create(name, type, items = []) {
        reload();
        const listId = id();
        commit({
          version: VERSION,
          lists: [
            ...state.lists,
            {
              id: listId,
              name: listName(name),
              type: listType(type),
              items: normalizeItems(items),
            },
          ],
        });
        return listId;
      },
      addItems(listId, items) {
        const valid = normalizeItems(items);
        update(listId, (list) => {
          valid.forEach((item) => {
            const existing = list.items.find((saved) =>
              item.variantId
                ? item.variantId === saved.variantId
                : !saved.variantId &&
                  item.sku.toLowerCase() === saved.sku.toLowerCase(),
            );
            if (existing)
              existing.quantity = quantity(existing.quantity + item.quantity);
            else list.items.push(item);
          });
        });
      },
      rename: (listId, name, type) =>
        update(listId, (list) => {
          list.name = listName(name);
          list.type = listType(type);
        }),
      updateQuantity: (listId, reference, value) =>
        update(listId, (list) => {
          const index = matchingItem(list, reference);
          list.items[index].quantity = quantity(value);
        }),
      removeItem: (listId, reference) =>
        update(listId, (list) => {
          const index = matchingItem(list, reference);
          list.items.splice(index, 1);
        }),
      delete(listId) {
        reload();
        if (!state.lists.some((list) => list.id === listId))
          fail("This list is no longer available.");
        commit({
          version: VERSION,
          lists: state.lists.filter((list) => list.id !== listId),
        });
      },
      export(listId) {
        return JSON.stringify(
          {
            version: VERSION,
            lists: state.lists.filter((list) => !listId || list.id === listId),
          },
          null,
          2,
        );
      },
      import(raw) {
        reload();
        const imported = parseBackup(raw).lists.map((list) => ({
          ...list,
          id: id(),
        }));
        commit({ version: VERSION, lists: [...state.lists, ...imported] });
        return imported.map((list) => list.id);
      },
      reset() {
        commit({ version: VERSION, lists: [] }, true);
      },
    };
  }
  function stageItems(storage, context, items, now = Date.now()) {
    const valid = normalizeItems(items);
    if (!valid.length) fail("Add products to this list before reviewing it.");
    try {
      if (!storage) throw new Error();
      storage.setItem(
        scopeKey(context, "list-review"),
        JSON.stringify({ version: VERSION, created: now, items: valid }),
      );
    } catch {
      fail(
        "Your browser cannot transfer this list to Quick order. Allow browser storage, or export the list and enter its SKUs in Quick order.",
      );
    }
  }
  function consumeItems(storage, context, now = Date.now()) {
    const key = scopeKey(context, "list-review");
    try {
      const raw = storage?.getItem(key);
      if (!raw) return [];
      storage.removeItem(key);
      if (raw.length > MAX_BYTES) return [];
      const data = JSON.parse(raw);
      if (
        data.version !== VERSION ||
        !Number.isFinite(data.created) ||
        data.created > now + 60000 ||
        now - data.created > 86400000
      )
        return [];
      return normalizeItems(data.items);
    } catch {
      return [];
    }
  }
  return {
    VERSION,
    MAX_BYTES,
    MAX_ITEMS,
    createStore,
    scopeKey,
    normalizeItems,
    parseBackup,
    stageItems,
    consumeItems,
  };
});
